import { getFilenameFromUrl, isIosDevice, openInNewTab } from "components/DownloadButton/utils";
import { getAuthTokenFromLocalStorage } from "hooks/useAuthToken";

export interface DownloadFileResult {
  success: boolean;
  filename: string;
}

/**
 * Shared fetch → blob → temporary `<a download>` click download, extracted from the
 * `SocketListener` WS-completion handlers (`handleGetVectorFile` / `handleUpscaleImage`) so
 * click-driven download surfaces (e.g. the Notifications bell/toaster, REF-6306) can reuse the
 * exact same cross-origin-safe pattern instead of a bare `<a href>` link, which silently fails for
 * cross-origin GCS URLs.
 *
 * iOS/iPadOS: `openInNewTab` MUST run synchronously, before the `fetch` await below closes the
 * user-activation window (REF-5796) — call this directly from the click handler, not from inside
 * another async function that already awaited something.
 */
export const downloadFile = async (url: string, filename?: string): Promise<DownloadFileResult> => {
  // REF-6306: a notification download target is a RELATIVE, authenticated API path
  // (`/v1/notifications/{id}/download`) that 302-redirects to the signed file after an entitlement
  // check — it is NOT a direct file URL. A bare `fetch(path)` would resolve it against the APP origin
  // (nginx → SPA index.html) and carry no auth, so we resolve it against the API host and send the
  // Bearer token. The browser drops the Authorization header on the cross-origin redirect to GCS, so
  // the signed URL still resolves. Absolute URLs (direct GCS links from the WS handlers) are untouched.
  const isApiPath = url.startsWith("/");
  const fetchUrl = isApiPath ? `${process.env.REACT_APP_API_URL}${url}` : url;

  if (isIosDevice() && !isApiPath) {
    openInNewTab(url);

    return { success: true, filename: filename || getFilenameFromUrl(url, "") };
  }

  const headers: Record<string, string> = {};
  if (isApiPath) {
    const token = getAuthTokenFromLocalStorage();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(fetchUrl, { headers });
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.statusText}`);
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error("Downloaded file is empty");
  }

  // For an API path the real filename lives on the redirected-to file URL, not the API path.
  const resolvedFilename = filename || getFilenameFromUrl(isApiPath ? response.url || fetchUrl : url, "");

  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.style.display = "none";
  link.href = objectUrl;
  link.download = resolvedFilename;

  document.body.appendChild(link);
  link.click();

  URL.revokeObjectURL(objectUrl);
  document.body.removeChild(link);

  return { success: true, filename: resolvedFilename };
};
